import React, { useState, useEffect, useMemo } from 'react';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface ReportRow {
  id: string;
  type: string;
  description: string;
  reportedBy: string;
  reportedUser: string;
  status: string;
  createdAt: string;
}

const statusLabels: Record<string, string> = {
  pending: 'Non traité',
  reviewed: 'En cours',
  resolved: 'Résolu',
};

const statusClasses: Record<string, string> = {
  pending: 'bg-red-50 text-red-700 border border-red-200',
  reviewed: 'bg-amber-50 text-amber-700 border border-amber-200',
  resolved: 'bg-blue-50 text-blue-700 border border-blue-200',
};

const Reports: React.FC = () => {
  const [reports, setReports] = useState<ReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('reports')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) {
          if (fetchError.message.includes('does not exist') || fetchError.code === '42P01') {
            setError('La table reports n\'existe pas encore dans la base.');
          } else {
            throw fetchError;
          }
          return;
        }

        setReports(
          (data || []).map((r: any) => ({
            id: r.id,
            type: r.reason || 'Autre',
            description: r.details || '',
            reportedBy: r.reporter_id || '',
            reportedUser: r.reported_user_id || '',
            status: r.status || 'pending',
            createdAt: new Date(r.created_at).toLocaleDateString('fr-FR'),
          }))
        );
      } catch (err) {
        logger.error('load_reports', 'Reports', { error: String(err) });
        setError('Impossible de charger les signalements.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      const matchSearch =
        search === '' ||
        r.description.toLowerCase().includes(search.toLowerCase()) ||
        r.type.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'all' || r.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [reports, search, statusFilter]);

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      await supabase.from('reports').update({ status: newStatus }).eq('id', id);
      setReports((prev) =>
        prev.map((r) => (r.id === id ? { ...r, status: newStatus } : r))
      );
    } catch (err) {
      alert(`Erreur: ${err}`);
    }
  };

  const untreatedCount = reports.filter((r) => r.status === 'pending').length;

  const columns: Column<ReportRow>[] = [
    {
      key: 'date',
      header: 'Date',
      render: (r) => <span className="text-gray-500 text-xs">{r.createdAt}</span>,
    },
    {
      key: 'type',
      header: 'Type',
      render: (r) => (
        <span className="inline-flex items-center px-2 py-0.5 bg-gray-50 text-gray-700 text-xs font-medium rounded-md border border-gray-200">
          {r.type}
        </span>
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (r) => (
        <p className="text-xs text-gray-600 max-w-xs truncate" title={r.description}>
          {r.description || '—'}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      render: (r) => (
        <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-full ${statusClasses[r.status] || 'bg-gray-100 text-gray-500'}`}>
          {statusLabels[r.status] || r.status}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (r) => (
        <div className="flex items-center gap-1.5">
          {r.status === 'pending' && (
            <>
              <button
                onClick={() => updateStatus(r.id, 'reviewed')}
                className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
              >
                Traiter
              </button>
              <button
                onClick={() => updateStatus(r.id, 'resolved')}
                className="px-2.5 py-1 text-xs font-medium text-gray-500 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              >
                Résoudre
              </button>
            </>
          )}
          {r.status === 'reviewed' && (
            <button
              onClick={() => updateStatus(r.id, 'resolved')}
              className="px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
            >
              ✓ Résoudre
            </button>
          )}
          {r.status === 'resolved' && (
            <span className="text-xs text-gray-400">Traité</span>
          )}
        </div>
      ),
    },
  ];

  const filterSlot = (
    <select
      value={statusFilter}
      onChange={(e) => setStatusFilter(e.target.value)}
      className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
    >
      <option value="all">Tous les statuts</option>
      <option value="pending">Non traité</option>
      <option value="reviewed">En cours</option>
      <option value="resolved">Résolu</option>
    </select>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl border border-amber-200 p-8 text-center">
        <span className="text-4xl block mb-3">📋</span>
        <p className="text-gray-700 font-medium">{error}</p>
        <p className="text-sm text-gray-400 mt-2">Cette fonctionnalité sera disponible une fois la table créée.</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-red-200 p-4 flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center text-2xl">
          🚩
        </div>
        <div>
          <p className="text-2xl font-bold text-red-600">{untreatedCount}</p>
          <p className="text-sm text-gray-500">signalements non traités</p>
        </div>
        <div className="ml-auto flex items-center gap-4 text-sm">
          <div className="text-center">
            <p className="font-semibold text-amber-600">{reports.filter(r => r.status === 'reviewed').length}</p>
            <p className="text-xs text-gray-400">en cours</p>
          </div>
          <div className="text-center">
            <p className="font-semibold text-blue-600">{reports.filter(r => r.status === 'resolved').length}</p>
            <p className="text-xs text-gray-400">résolus</p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filtered}
        keyExtractor={(r) => r.id}
        searchable
        searchPlaceholder="Rechercher par type, description..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />
    </div>
  );
};

export default Reports;
